/**
 * Search Results Component
 *
 * Displays search results in a clean, Google-like layout without card borders.
 */

import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useHref } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { SearchResultItem, SearchResponse } from '../../types/search';
import { getSourceLabel } from '../../types/search';
import { getSourceFallbackIcon } from '../../utils/sourceIcons';
import { SUNRISE_SOURCE, sunriseTicketPath } from '../../utils/sunrise';
import { StatusMessage } from './StatusMessage';

// -----------------------------------------------------------------------------
// Result Card (Google-style)
// -----------------------------------------------------------------------------

interface ResultCardProps {
  result: SearchResultItem;
  untitledText: string;
  openInNewTabText: string;
  ticketNumberText: (number: string) => string;
}

function ResultCard({ result, untitledText, openInNewTabText, ticketNumberText }: ResultCardProps) {
  // Legacy Sunrise tickets open in-app; the sunrise-crm: link needs a rarely installed client.
  const ticketPath =
    result.source === SUNRISE_SOURCE
      ? sunriseTicketPath(result.parent_document_id ?? result.id)
      : null;
  const rawTicketNumber = result.source_fields?.ticket_number;
  const ticketNumber =
    ticketPath && (typeof rawTicketNumber === 'string' || typeof rawTicketNumber === 'number')
      ? ticketNumberText(String(rawTicketNumber))
      : null;
  const breadcrumb = result.breadcrumb?.filter(Boolean).join(' > ');

  const linkProps = ticketPath
    ? { component: RouterLink, to: ticketPath }
    : result.url
      ? { component: 'a' as const, href: result.url }
      : null;
  const ticketHref = useHref(ticketPath ?? '.');
  const newTabHref = ticketPath ? ticketHref : result.url;

  return (
    <Box
      sx={{
        position: 'relative',
        '& .open-in-new-tab': { opacity: 0, transition: 'opacity 150ms' },
        '&:hover .open-in-new-tab, & .open-in-new-tab:focus-visible': { opacity: 1 },
        // Touch devices have no hover, so keep the button visible there
        '@media (hover: none)': { '& .open-in-new-tab': { opacity: 1 } },
      }}
    >
      <ButtonBase
        {...(linkProps ?? { component: 'div' as const })}
        disabled={!linkProps}
        sx={{
          display: 'block',
          textAlign: 'left',
          width: '100%',
          py: 2,
          px: 1,
          borderRadius: 0.5,
          color: 'inherit',
          textDecoration: 'none',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Stack spacing={1.25}>
          {/* Row 1: Thumbnail + source label + breadcrumb */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pr: 5 }}>
            <Avatar
              variant="circular"
              src={getSourceFallbackIcon(result.source, result.source_type)}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, lineHeight: 1.2, display: 'block' }}
              >
                {getSourceLabel(result.source)}
              </Typography>
              {ticketNumber && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 500,
                  }}
                >
                  {ticketNumber}
                </Typography>
              )}
              {breadcrumb && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    lineHeight: 1.2,
                    fontWeight: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 500,
                  }}
                >
                  {breadcrumb}
                </Typography>
              )}
            </Box>
          </Stack>

          {/* Row 2: Title */}
          <Typography
            variant="h3"
            component="h3"
            color="primary"
            sx={{
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {result.title || untitledText}
          </Typography>

          {/* Row 3: Summary */}
          {result.summary && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {result.summary.length > 250
                ? result.summary.substring(0, 250) + '...'
                : result.summary}
            </Typography>
          )}
        </Stack>
      </ButtonBase>

      {/* Sibling of the card link, since interactive elements must not be nested */}
      {newTabHref && (
        <Tooltip title={openInNewTabText}>
          <IconButton
            component="a"
            href={newTabHref}
            className="open-in-new-tab"
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            aria-label={openInNewTabText}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'text.secondary' }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// -----------------------------------------------------------------------------
// Loading Skeleton (Google-style)
// -----------------------------------------------------------------------------

function ResultSkeleton() {
  return (
    <Box sx={{ py: 2, px: 1 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Skeleton variant="rounded" width={40} height={40} />
        <Box>
          <Skeleton variant="text" width={100} height={16} />
          <Skeleton variant="text" width={220} height={14} />
        </Box>
      </Stack>
      <Skeleton variant="text" width="55%" height={26} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="100%" height={18} />
      <Skeleton variant="text" width="85%" height={18} />
    </Box>
  );
}

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

interface SearchResultsProps {
  results: SearchResponse | null;
  isLoading?: boolean;
  query?: string;
}

export function SearchResults({ results, isLoading = false, query }: SearchResultsProps) {
  const { t } = useTranslation('search');

  // Loading state
  if (isLoading) {
    return (
      <Stack spacing={2}>
        {[1, 2, 3].map((i) => (
          <ResultSkeleton key={i} />
        ))}
      </Stack>
    );
  }

  // No search yet
  if (!results && !query) {
    return null;
  }

  // No results
  if (results && results.results.length === 0) {
    return <StatusMessage event="no-results" />;
    
  }

  // Helper for translated strings passed to ResultCard
  const untitledText = t('results.untitled');
  const openInNewTabText = t('results.openInNewTab');
  const ticketNumberText = (number: string) => t('ticket.number', { number });

  // Show results
  if (results) {
    return (
      <Box>
        {/* Results Header */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            dangerouslySetInnerHTML={{
              __html: results.took_ms
                ? t('results.foundWithTime', { total: results.total.toLocaleString(), time: results.took_ms })
                : t('results.found', { total: results.total.toLocaleString() })
            }}
          />
          {results.acl_status === 'limited' && (
            <Tooltip title={t('results.limited.tooltip')} arrow placement="bottom-start">
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                icon={<InfoOutlinedIcon fontSize="small" />}
                label={t('results.limited.label')}
                aria-label={t('results.limited.tooltip')}
                sx={{
                  // Subtle M3 tonal feel: low emphasis, soft border, no shouting colour.
                  borderColor: 'divider',
                  bgcolor: (theme) => theme.palette.action.hover,
                  color: 'text.secondary',
                  '& .MuiChip-icon': { color: 'warning.main' },
                  fontWeight: 500,
                }}
              />
            </Tooltip>
          )}
        </Box>

        {/* Results List */}
        <Stack spacing={0} >
          {results.results.map((result) => (
            <ResultCard 
              key={result.id} 
              result={result} 
              untitledText={untitledText}
              openInNewTabText={openInNewTabText}
              ticketNumberText={ticketNumberText}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  return null;
}
